import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { L3_PATHS } from 'Deer/constants/routes';
import { setL2Visible } from 'Deer/slice/DeerNav.slice';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { closeSidebar as closeLeftNavbar, openSidebar as openLeftNavbar } from 'reducers/sidebarNav-slice';
import { LevelUpSidePanelContextProvider } from 'screens/oneOnOne/levelUp/competencySidePanel/context/provider';
import {
  check1on1TranscriptionEnabled,
  checkHideDemoOneOnOneSections,
  checkNewNavEnabled,
} from 'utils/helpers/featureFlags';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { getQueryParams } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { Header } from './InstancePage/common/Header';
import { oneOneOneReactQueryKeys } from './InstancePage/constants/oneOnOneReactQueryKeys';
import { AgendaList } from './InstancePage/features/Agenda/AgendaList';
import { OneOnOneAgendaCreateOrUpdateI, useOneOnOneAgendaQuery } from './InstancePage/features/Agenda/hooks/dataHooks';
import { AgendaLibrary } from './InstancePage/features/AgendaLibrary/AgendaLibrary';
import { useOneOnOneAgendaLibraryQuery } from './InstancePage/features/AgendaLibrary/hooks/dataHooks';
import { addAgendaFromLibraryToInstance } from './InstancePage/features/AgendaLibrary/services';
import BotNotificationModal from './InstancePage/features/BotNotification/BotNotificationModal';
import FooterRatingBar from './InstancePage/features/FooterRatingBar/FooterRatingBar';
import InitiativesList from './InstancePage/features/Initiatives/InitiativesList';
import { MeetingDetails } from './InstancePage/features/MeetingDetails/MeetingDetails';
import { MeetingNotes } from './InstancePage/features/Notes/MeetingNotes';
import { RightSidebar } from './InstancePage/features/RIghtSidebar/RightSidebar';
import { OneOnOneRating } from './InstancePage/features/Rating/OneOnOneRating';
import { useOneOnOneInstanceDetails, useOneOnOneInstanceDetailsQuery } from './InstancePage/hooks/dataHooks';
import { setIsExpandedOneOnOneSidebar, useOneOnOneRightSidebar } from './InstancePage/slice/OneOnOneInstancePageSlice';
import { useOneOnOneGeneralSettingsDetails } from './SettingsPage/hooks/dataHooks';
import useOneOnOnePageParams from './hooks/useOneOnOnePageParams';

const Container = styled(Flex)`
  width: 810px;
  @media (min-width: 1311px) and (max-width: 1600px) {
    margin: ${({ $isSidebarExpanded, $isNewNav }) =>
      $isSidebarExpanded ? `22px ${$isNewNav ? '70px' : '140px'}` : '22px auto'};
  }
  @media (max-width: 1310px) {
    margin: ${({ $isSidebarExpanded }) => ($isSidebarExpanded ? '22px 20px' : '22px auto')};
  }
  margin: 22px auto;
`;

const NetchexContainer = styled(Flex)`
  width: 810px;
  margin: 22px auto;
  @media (min-width: 1311px) and (max-width: 1600px) {
    width: 650px;
    margin: ${({ $isSidebarExpanded }) => ($isSidebarExpanded ? `22px 40px` : '22px auto')};
  }
  @media (max-width: 1310px) {
    width: 560px;
    margin: ${({ $isSidebarExpanded }) => ($isSidebarExpanded ? '22px 20px' : '22px auto')};
  }
`;

const InstancePage = () => {
  const history = useHistory();
  const toast = useToast();
  const oneOnOneRatingRef = useRef(null);
  const [isBotNotificationBarOpen, setIsBotNotificationBarOpen] = useState(true);
  const [isRatingBarOpen, setIsRatingBarOpen] = useState(true);
  const { masterId, instanceId } = useOneOnOnePageParams();
  const { data, isLoading, isError } = useOneOnOneInstanceDetailsQuery({
    masterId,
    instanceId,
  });
  const queryClient = useQueryClient();
  const { meetingLink, completionStatus } =
    useOneOnOneInstanceDetails({
      masterId,
      instanceId,
    }) ?? {};

  const { data: settingsPageData } = useOneOnOneGeneralSettingsDetails();
  const { search: queryString } = useLocation();

  const { agendaTextId } = getQueryParams(queryString);
  const isAgendaTextAddedFromURL = useRef(false);

  const { data: agendaItems } = useOneOnOneAgendaQuery({
    masterId,
    instanceId,
  });

  const { data: themeAgenda } = useOneOnOneAgendaLibraryQuery({
    masterId,
    instanceId,
    type: 'THEME',
    enabled: true,
  });

  const { data: lifecycleAgenda } = useOneOnOneAgendaLibraryQuery({
    masterId,
    instanceId,
    type: 'LIFECYCLE_MOMENT',
    enabled: true,
  });

  const instancePageData = useMemo(
    () => [...(themeAgenda ?? []), ...(lifecycleAgenda ?? [])],
    [themeAgenda, lifecycleAgenda]
  );

  const handleAddAgendaTextFromParams = useCallback(
    async (agenda: { id?: number; agendaText?: string }) => {
      const addedAgenda = await addAgendaFromLibraryToInstance({
        masterId,
        instanceId,
        agendaText: agenda.agendaText,
        libraryAgendaId: agenda.id,
      });

      if (addedAgenda) {
        queryClient.setQueryData(
          [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
          (old: OneOnOneAgendaCreateOrUpdateI[]) => (Array.isArray(old) ? [...old, addedAgenda] : [addedAgenda])
        );
        toast({
          title: 'Agenda has been added',
          status: 'success',
        });
        queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
      }
    },
    [masterId, instanceId, queryClient, toast]
  );

  useEffect(() => {
    if (agendaTextId && instancePageData && agendaItems && isAgendaTextAddedFromURL.current === false) {
      const agenda = instancePageData.find((curAgenda) => curAgenda.id === Number(agendaTextId));
      const isAgendaAlreadyAdded = Boolean(
        agendaItems?.find((curAgenda) => curAgenda.agendaText === agenda?.agendaText)
      );
      isAgendaTextAddedFromURL.current = isAgendaAlreadyAdded;

      if (agenda && !isAgendaAlreadyAdded) {
        isAgendaTextAddedFromURL.current = true;
        handleAddAgendaTextFromParams(agenda);
      }
    }
  }, [agendaTextId, instancePageData, handleAddAgendaTextFromParams, agendaItems]);

  // here these conditions needs to be satisfy to show notification bar
  const showBotNotificationBar =
    check1on1TranscriptionEnabled() &&
    isBotNotificationBarOpen &&
    meetingLink &&
    settingsPageData?.includeTranscriptionBot &&
    completionStatus === 'YET_TO_START';

  const dispatch = useDispatch();

  const wrapperRef = React.useRef(null);

  const setIsExpanded = (isExpanded: boolean) => {
    if (isExpanded) {
      // eslint-disable-next-line no-unused-expressions
      checkNewNavEnabled()
        ? dispatch(
            setL2Visible({
              isL2Visible: false,
            })
          )
        : dispatch(closeLeftNavbar());
    } else {
      // eslint-disable-next-line no-unused-expressions
      checkNewNavEnabled()
        ? dispatch(
            setL2Visible({
              isL2Visible: true,
            })
          )
        : dispatch(openLeftNavbar());
    }
    dispatch(setIsExpandedOneOnOneSidebar(isExpanded));
  };

  const isExpanded = useOneOnOneRightSidebar();

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        page: '1:1 Instance Page',
      },
    });
  }, []);

  useEffect(() => {
    const path = checkNewNavEnabled() ? L3_PATHS['my-data'].oneOnOne.myOneOnOne : routes.oneOnOne.audit;
    if (isError) {
      toast({
        title: 'We faced an error while accessing the 1:1. Please try again after some time',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      history.push(path);
    }
  }, [history, isError, toast]);

  const ratingBarClickHandler = () => {
    oneOnOneRatingRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
    setIsRatingBarOpen(false);
  };

  // Close the right sidebar when the component unmounts
  useEffect(
    () => () => {
      setIsExpanded(false);
    },
    []
  );

  const isNetchex = process.env.PUBLIC_NETCHEX_LOGIN_FLOW === 'true';

  const Wrapper = isNetchex ? NetchexContainer : Container;

  if (isLoading || isError) {
    return null;
  }

  return (
    <Flex flexDir="column" ref={wrapperRef}>
      <Header />
      {showBotNotificationBar && !isMobileView() && (
        <BotNotificationModal setIsBotNotificationBarOpen={setIsBotNotificationBarOpen} />
      )}
      <Wrapper
        overflow="hidden"
        flexDir="column"
        $isSidebarExpanded={isExpanded}
        $isNewNav={checkNewNavEnabled()}
        p="1px"
        w={isMobileView() ? '100% !important' : '810px !important'}
      >
        <MeetingDetails />
        {!checkHideDemoOneOnOneSections() && <AgendaList agendaLibrary={instancePageData ?? []} />}
        {!checkHideDemoOneOnOneSections() && <InitiativesList />}
        <MeetingNotes />
        {data?.isShowRating && (
          <div ref={oneOnOneRatingRef}>
            <OneOnOneRating />
          </div>
        )}
        {!isMobileView() && <AgendaLibrary />}

        {!isMobileView() && (
          <LevelUpSidePanelContextProvider>
            <RightSidebar
              isBotNotificationBarOpen={meetingLink && isBotNotificationBarOpen}
              setIsExpanded={setIsExpanded}
              isExpanded={isExpanded}
              wrapperRef={wrapperRef}
            />
          </LevelUpSidePanelContextProvider>
        )}
      </Wrapper>

      {data?.isShowRating && isRatingBarOpen && !isMobileView() && (
        <FooterRatingBar onClick={ratingBarClickHandler} setIsRatingBarOpen={setIsRatingBarOpen} />
      )}
    </Flex>
  );
};

export { InstancePage };
